import React, { useEffect, useState } from 'react';

/**
 * OnboardingCompleteOverlay (iter164)
 *
 * Apple-style "big green checkmark" celebration that fires the moment
 * the user's `is_onboarded` flag flips from false → true in the same
 * session. Sits as a full-screen modal overlay; auto-dismisses after
 * 2 s and reveals the unlocked app underneath.
 *
 * Animation is hand-rolled with CSS keyframes (no Motion lib import
 * needed — keeps the bundle lean for the one-time celebration).
 *
 * Props:
 *   onDone   — invoked AFTER the dismiss animation completes so the
 *              parent can decide what to do next (typically nothing;
 *              the regular routes already render under the overlay).
 */
const OnboardingCompleteOverlay = ({ onDone }) => {
  const [phase, setPhase] = useState('enter'); // 'enter' | 'hold' | 'leave'

  useEffect(() => {
    // enter (0–650 ms) → hold (650–1850 ms) → leave (1850–2250 ms)
    const t1 = setTimeout(() => setPhase('hold'), 650);
    const t2 = setTimeout(() => setPhase('leave'), 1850);
    const t3 = setTimeout(() => onDone?.(), 2250);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onDone]);

  const overlayOpacity = phase === 'leave' ? 0 : 1;
  const ringScale = phase === 'enter' ? 0.6 : 1;
  const tickDashOffset = phase === 'enter' ? 56 : 0;

  return (
    <div
      data-testid="onboarding-complete-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(8, 14, 10, 0.72)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        opacity: overlayOpacity,
        transition: 'opacity 380ms ease',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          width: 144,
          height: 144,
          borderRadius: '9999px',
          backgroundColor: '#22c55e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `scale(${ringScale})`,
          transition: 'transform 560ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          boxShadow: '0 24px 60px -12px rgba(34, 197, 94, 0.6), 0 0 0 8px rgba(34, 197, 94, 0.15)',
        }}
      >
        <svg width="84" height="84" viewBox="0 0 56 56" fill="none">
          <path
            d="M14 29.5 L24 39 L43 18"
            stroke="white"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="56"
            strokeDashoffset={tickDashOffset}
            style={{ transition: 'stroke-dashoffset 520ms ease 220ms' }}
          />
        </svg>
      </div>
      <p
        style={{
          marginTop: 32,
          color: 'white',
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          opacity: phase === 'enter' ? 0 : 1,
          transform: phase === 'enter' ? 'translateY(8px)' : 'translateY(0)',
          transition: 'opacity 420ms ease 480ms, transform 420ms ease 480ms',
        }}
      >
        You're all set
      </p>
    </div>
  );
};

export default OnboardingCompleteOverlay;
